import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Routes } from "constants/routes";
import Flex from "app/components/Flex";
import LoaderCentered from "app/components/Loader/LoaderCentered";
import { useGetUserProfile } from "hooks/User/useGetUserProfile";
import { BirthdayStep } from "./components/BirthdayStep";
import { NameStep } from "./components/NameStep";
import { UsernameStep } from "./components/UsernameStep";
import { ProfilePhotoStep } from "./components/ProfilePhotoStep";
import { WelcomeStep } from "./components/WelcomeStep";
import OnboardingNav from "./components/OnboardingNav";
import { OnboardingStep, UserData } from "./constants";
import styles from "./styles.module.scss";

const Wrapper = styled(Flex).attrs({
  height: "100%",
  overflow: "hidden",
})``;

const ContentWrapper = styled(Flex)`
  display: flex;
  width: 100%;
  overflow: auto;
`;

const Onboarding: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const stepParam = searchParams.get("step");

  // Get current step from URL or default to Birthday
  const currentStep = Object.values(OnboardingStep).includes(
    stepParam as OnboardingStep
  )
    ? (stepParam as OnboardingStep)
    : OnboardingStep.Birthday;

  // Fetch user data from GraphQL
  const { profileData, loading } = useGetUserProfile();
  const [initialized, setInitialized] = useState(false);

  const [formData, setFormData] = useState<UserData>({
    birthday: "",
    name: "",
    username: "",
    photoURL: "",
  });

  // Update form data when user data is loaded
  useEffect(() => {
    if (profileData?.birthDate) {
      setFormData(prev => ({
        ...prev,
        birthday: profileData.birthDate || "",
        name: profileData.name || "",
        username: profileData.username || "",
        photoURL: profileData.photoURL || "",
      }));
      setInitialized(true);
    } else if (!loading) {
      // If no user data and not loading, we can consider it initialized
      setInitialized(true);
    }
  }, [profileData, loading]);

  const handleStepComplete = (stepData?: Partial<UserData>) => {
    setFormData(prev => ({ ...prev, ...stepData }));

    // Update URL with next step
    switch (currentStep) {
      case OnboardingStep.Birthday:
        history.push(`${Routes.onboarding}?step=${OnboardingStep.Name}`);
        break;
      case OnboardingStep.Name:
        history.push(`${Routes.onboarding}?step=${OnboardingStep.Username}`);
        break;
      case OnboardingStep.Username:
        history.push(
          `${Routes.onboarding}?step=${OnboardingStep.ProfilePhoto}`
        );
        break;
      case OnboardingStep.ProfilePhoto:
        history.push(`${Routes.onboarding}?step=${OnboardingStep.Welcome}`);
        break;
      case OnboardingStep.Welcome:
        history.push(Routes.community); // Navigate to community after completion
        break;
      default:
        // Handle unexpected step by resetting to beginning
        console.error("Unexpected onboarding step:", currentStep);
        history.push(`${Routes.onboarding}?step=${OnboardingStep.Birthday}`);
        break;
    }
  };

  if (loading || !initialized) {
    return (
      <Wrapper color="black">
        <OnboardingNav />
        <ContentWrapper>
          <div className={styles.onboarding}>
            <div className={styles.content}>
              <LoaderCentered />
            </div>
          </div>
        </ContentWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper color="black">
      <OnboardingNav />
      <ContentWrapper>
        <div className={styles.onboarding}>
          <div className={styles.content}>
            {currentStep === OnboardingStep.Birthday && (
              <BirthdayStep
                onComplete={handleStepComplete}
                initialValue={formData.birthday}
              />
            )}
            {currentStep === OnboardingStep.Name && (
              <NameStep
                onComplete={handleStepComplete}
                initialValue={formData.name}
              />
            )}
            {currentStep === OnboardingStep.Username && (
              <UsernameStep
                onComplete={handleStepComplete}
                name={formData.name}
              />
            )}
            {currentStep === OnboardingStep.ProfilePhoto && (
              <ProfilePhotoStep
                onComplete={handleStepComplete}
                initialValue={formData.photoURL}
              />
            )}
            {currentStep === OnboardingStep.Welcome && (
              <WelcomeStep
                userData={{
                  name: formData.name,
                  username: formData.username,
                  photoURL: formData.photoURL || null,
                }}
                onComplete={handleStepComplete}
              />
            )}
          </div>
        </div>
      </ContentWrapper>
    </Wrapper>
  );
};

export default Onboarding;
